import { createContext, useState } from 'react'

import { Modal } from 'antd'
import { useSelector } from 'react-redux'
import { isFunction } from 'lodash'

import SingleStudentForm from './SingleStudentForm'
import StudentCardPicker from './StudentCardPicker'
import { firstOrDefault, getIdOrNull, isNonEmptyArray, isObject, isOptimistic } from '../../../utils'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { Student, StudentProperties as Properties } from '../../../utils/entities/student'
import StudentSearchBar from './StudentSearchBar'

export const SearchStudentFormModalContext = createContext()

export const Permissions = Object.freeze({
  WRITE: 'write',
  FILL: 'fill',
  READ_ONLY: 'readOnly',
  GOD: 'god'
})

export const Modes = Object.freeze({
  DEFAULT: 'default',
  PICKER: 'picker',
  FORM: 'form'
})

const DEFAULT_FORM_HANDLER = { data: null, callback: null, mode: Modes.DEFAULT }

const SearchStudentFormModalProvider = ({ children, highlighted, school, permission = Permissions.READ_ONLY }) => {
  const t = useSelector(selectTranslate)

  const [formHandler, setFormHandler] = useState(DEFAULT_FORM_HANDLER)

  const updateStudent = student => setFormHandler(({ ...formHandler, data: { ...formHandler.data, ...student } }))

  const handleSearch = (response, email) => {
    const data = response.data

    if (data.length === 0) {
      setFormHandler({ ...formHandler, data: new Student({ email }), mode: Modes.FORM })
    } else {
      const student = isNonEmptyArray(response.data) ? new Student(firstOrDefault(response.data)) : null

      if (student) {
        handleStudentSelection(student)
      } else {
        setFormHandler(handler => ({ ...handler, data: new Student({ email }), mode: Modes.DEFAULT }))
      }
    }
  }

  const handleCloseModal = () => {
    setFormHandler(DEFAULT_FORM_HANDLER)
  }

  const handleStudentSelection = student => {
    const sectionInSelectedSchool = student.sections.find(section => getIdOrNull(section.school) === getIdOrNull(school))

    if (getIdOrNull(sectionInSelectedSchool) !== null) {
      handleFormSubmit({ ...student, [Properties.SECTIONS]: [sectionInSelectedSchool] })
    } else {
      setFormHandler(handler => ({ ...handler, mode: Modes.PICKER, data: student }))
    }
  }

  const handleFormSubmit = student => {
    if (isFunction(formHandler.callback)) {
      formHandler.callback(student)
    }

    handleCloseModal()
  }

  const handleSelectCard = section => {
    handleFormSubmit({ ...formHandler.data, [Properties.SECTIONS]: [section] })
  }

  return (
    <>
      <SearchStudentFormModalContext.Provider value={{ formHandler, setFormHandler }}>
        {children}
      </SearchStudentFormModalContext.Provider>
      <Modal
        className='form-modal-without-footer'
        title={t('search_student_modal.title')}
        visible={isObject(formHandler.data)}
        destroyOnClose
        footer={null}
        onCancel={handleCloseModal}
      >
        <div className='m-8 pd-8'>
          <StudentSearchBar className='flex-col' showNotFound onSubmit={handleSearch} />
        </div>

        {formHandler.mode === Modes.PICKER && (
          <StudentCardPicker
            highlighted={highlighted}
            student={formHandler.data}
            onConfirm={handleSelectCard}
            onCancel={handleCloseModal}
          />
        )}

        {formHandler.mode === Modes.FORM && (
          <div className='mt-8'>
            <h3 className='pdl-16'>{t('search_student_modal.new_student')}</h3>
            <SingleStudentForm
              data={formHandler.data}
              permission={isOptimistic(formHandler.data) ? Permissions.WRITE : permission}
              onChange={updateStudent}
              onSubmit={handleFormSubmit}
              onCancel={handleCloseModal}
            />
          </div>
        )}
      </Modal>
    </>
  )
}

export default SearchStudentFormModalProvider
